import { createContext, useContext, useState, useEffect } from "react";
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  createUserWithEmailAndPassword 
} from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "@/lib/firebase";

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

const ADMIN_EMAILS = ["daniel@havanagroups.com"];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        try {
          const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            const userEmail = firebaseUser.email?.toLowerCase() || "";
            const isAdmin = ADMIN_EMAILS.includes(userEmail);
            
            if (isAdmin) {
              const updatedData = { ...data, id: firebaseUser.uid, role: "COO", permissions: ["all"], department: "Executive" };
              setUserData(updatedData);
              try {
                await updateDoc(doc(db, "users", firebaseUser.uid), {
                  name: data.name || firebaseUser.email?.split("@")[0],
                  email: firebaseUser.email,
                  role: "COO",
                  permissions: ["all"],
                  department: "Executive"
                });
              } catch (e) {
                console.error("Error updating user role:", e);
              }
            } else {
              setUserData({ ...data, id: firebaseUser.uid });
            }
          } else {
            const email = firebaseUser.email || "";
            const name = email.split("@")[0];
            const isAdmin = ADMIN_EMAILS.includes(email.toLowerCase());
            try {
              await setDoc(doc(db, "users", firebaseUser.uid), {
                name,
                email,
                role: isAdmin ? "COO" : "Team Member",
                permissions: isAdmin ? ["all"] : ["view"],
                department: "Executive",
                createdAt: new Date().toISOString()
              });
            } catch (e) {}
            setUserData({
              name,
              email,
              role: isAdmin ? "COO" : "Team Member",
              permissions: isAdmin ? ["all"] : ["view"],
              department: "Executive",
              id: firebaseUser.uid
            });
          }
        } catch (error) {
          const email = firebaseUser.email || "";
          const name = email.split("@")[0];
          const isAdmin = ADMIN_EMAILS.includes(email.toLowerCase());
          setUserData({
            name,
            email,
            role: isAdmin ? "COO" : "Team Member",
            permissions: isAdmin ? ["all"] : ["view"],
            department: isAdmin ? "Executive" : "General",
            id: firebaseUser.uid
          });
        }
      } else {
        setUser(null);
        setUserData(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email, password, totpCode = null) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return { success: true, user: result.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const hasPermission = (permission) => {
    if (!userData) return false;
    if (userData.permissions?.includes("all")) return true;
    return userData.permissions?.includes(permission);
  };

  const refreshUserData = async () => {
    if (user) {
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          setUserData(userDoc.data());
        }
      } catch (error) {}
    }
  };

  const value = {
    user,
    userData,
    loading,
    login,
    logout,
    hasPermission,
    refreshUserData,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};